import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { AccentBar } from './components/AccentBar';
import { DevAnnotation } from './components/DevAnnotation';
import { ScrollToTop } from './components/ScrollToTop';
import { RequireAccess } from './components/RequireAccess';
import SignIn from './routes/SignIn';
import AuthCallback from './routes/AuthCallback';
import ResetPassword from './routes/ResetPassword';
import Feed from './routes/Feed';
import Archive from './routes/Archive';
import Post from './routes/Post';
import Profile from './routes/Profile';
import PublicProfile from './routes/PublicProfile';
import Admin from './routes/Admin';
import NewPost from './routes/NewPost';
import BillingPortal from './routes/BillingPortal';
import BillingSuccess from './routes/BillingSuccess';
import Subscribe from './routes/Subscribe';
import Onboarding from './routes/Onboarding';
import Welcome from './routes/Welcome';
import SalesRoom from './routes/SalesRoom';
import Calendar from './routes/Calendar';
import Watchlist from './routes/Watchlist';
import DesignSystem from './routes/_DesignSystem';
import { useSession } from './lib/session';
import type { Profile as ProfileType } from './lib/types';

/**
 * Team/staff tags that bypass the onboarding gate. These accounts often
 * predate the wizard or are created programmatically without a handle;
 * forcing them through the wizard would block admin work.
 */
const BYPASS_TAGS = new Set(['founder', 'team', 'staff']);

function needsOnboarding(profile: ProfileType | null): boolean {
  if (!profile) return false;
  // Admin/developer + tagged staff bypass the gate entirely.
  if (profile.role === 'admin' || profile.role === 'developer') return false;
  if (profile.tags?.some((t) => BYPASS_TAGS.has(t))) return false;
  // Gate on the EXPLICIT completion flag, not on display_name + handle being
  // present. The wizard saves those at step 1, so keying off them let a member
  // who reloaded mid-wizard skip the rest of the flow (password, etc.) and land
  // on the feed. The flag only flips on the wizard's final commit, so a reload
  // keeps them in onboarding until it's genuinely finished. (Existing members
  // were backfilled to completed in migration 078.)
  return profile.onboarding_completed !== true;
}

/**
 * One-time founding-member welcome screen. Shown to a founding member (a
 * regular user, not staff) who hasn't dismissed it yet, AFTER onboarding.
 * Existing accounts were backfilled to seen_welcome=true (migration 053), so
 * only true first-logins hit this.
 */
function needsWelcome(profile: ProfileType | null): boolean {
  if (!profile) return false;
  if (profile.role !== 'user') return false;
  return Boolean(profile.is_founding_member) && profile.seen_welcome !== true;
}

/**
 * Staff preview escape hatch. Lets an admin/developer open /onboarding or
 * /welcome with `?preview=1` to walk the flow WITHOUT being bounced back to
 * the feed - so testing the wizard doesn't require deleting + re-inviting a
 * member each time. The pages themselves treat preview as read-only (no
 * profile writes / no "seen" flag), so previewing never mutates the staffer's
 * own account.
 */
function isStaffPreview(profile: ProfileType | null, search: string): boolean {
  if (!profile) return false;
  const staff = profile.role === 'admin' || profile.role === 'developer';
  return staff && new URLSearchParams(search).get('preview') === '1';
}

function LoadingSplash() {
  return (
    <div
      role="status"
      aria-label="Loading"
      style={{ minHeight: '60dvh', display: 'grid', placeItems: 'center', color: 'var(--text-soft)' }}
    >
      …
    </div>
  );
}

/**
 * Gate that enforces the first-run wizard for any authed user who hasn't
 * set a display_name + handle yet. Used to wrap the AppShell - /onboarding
 * itself has its own rules (see OnboardingGate).
 */
function RequireOnboarded({ children }: { children: JSX.Element }) {
  const { loading, isAuthed, profile } = useSession();
  const location = useLocation();
  if (loading) return <LoadingSplash />;
  if (!isAuthed) {
    return <Navigate to="/sign-in" replace state={{ from: location }} />;
  }
  if (needsOnboarding(profile)) {
    return <Navigate to="/onboarding" replace state={{ from: location }} />;
  }
  if (needsWelcome(profile)) {
    return <Navigate to="/welcome" replace />;
  }
  return children;
}

/**
 * /welcome is only for a founding member who hasn't seen it yet. Unauthed →
 * sign-in; still needs onboarding → wizard; already seen (or not eligible) →
 * home. Mirrors OnboardingGate.
 */
function WelcomeGate({ children }: { children: JSX.Element }) {
  const { loading, isAuthed, profile } = useSession();
  const location = useLocation();
  const preview = isStaffPreview(profile, location.search);
  if (loading) return <LoadingSplash />;
  if (!isAuthed) return <Navigate to="/sign-in" replace />;
  if (preview) return children;
  if (needsOnboarding(profile)) return <Navigate to="/onboarding" replace />;
  if (!needsWelcome(profile)) return <Navigate to="/" replace />;
  return children;
}

/**
 * /onboarding is only for authed users who haven't finished the wizard.
 * Unauthed → sign-in. Already-onboarded → home.
 */
function OnboardingGate({ children }: { children: JSX.Element }) {
  const { loading, isAuthed, profile } = useSession();
  const location = useLocation();
  const preview = isStaffPreview(profile, location.search);
  if (loading) return <LoadingSplash />;
  if (!isAuthed) return <Navigate to="/sign-in" replace />;
  if (!needsOnboarding(profile) && !preview) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* Living design system (public, no auth) - shared with the team at
            /_ds so they can browse every token + primitive and leave notes.
            Source of truth: changing a token/primitive updates the whole app. */}
        <Route path="/_ds" element={<DesignSystem />} />
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/onboarding"
          element={
            <OnboardingGate>
              <Onboarding />
            </OnboardingGate>
          }
        />
        <Route
          path="/welcome"
          element={
            <WelcomeGate>
              <Welcome />
            </WelcomeGate>
          }
        />

        <Route
          element={
            <RequireOnboarded>
              <AppShell />
            </RequireOnboarded>
          }
        >
          {/* Always accessible to authed users - no subscription required */}
          <Route path="profile" element={<Profile />} />
          <Route path="subscribe" element={<Subscribe />} />
          <Route path="billing/portal" element={<BillingPortal />} />
          <Route path="billing/success" element={<BillingSuccess />} />

          {/* Paywalled surfaces */}
          <Route index element={<RequireAccess><Feed /></RequireAccess>} />
          <Route path="archive" element={<RequireAccess><Archive /></RequireAccess>} />
          <Route path="p/:slug" element={<RequireAccess><Post /></RequireAccess>} />
          {/* A/B test: the stacked (old-arrangement) lot page. Same Post
              component, layout="stacked" - bid box below, description above. */}
          <Route path="saleroom2/p/:slug" element={<RequireAccess><Post layout="stacked" /></RequireAccess>} />
          <Route path="u/:handle" element={<RequireAccess><PublicProfile /></RequireAccess>} />
          <Route path="admin" element={<Admin />} />
          <Route path="admin/new" element={<NewPost />} />
          <Route path="admin/edit/:slug" element={<NewPost />} />

          {/* Sales Room / Calendar / Favorites - the new components self-wrap
              in <RequireAccess>, so registration here is just the bare path. */}
          <Route path="saleroom" element={<SalesRoom />} />
          {/* A/B variant grid: same Saleroom, but lot cards link to the stacked
              lot pages under /saleroom2/p. */}
          <Route path="saleroom2" element={<SalesRoom lotHrefBase="/saleroom2/p" />} />
          {/* Old hyphenated path → canonical /saleroom (kept for any stale links). */}
          <Route path="sales-room" element={<Navigate to="/saleroom" replace />} />
          <Route path="calendar" element={<Calendar />} />
          <Route path="favorites" element={<Watchlist />} />
          {/* Old /watchlist path → canonical /favorites (kept for stale links). */}
          <Route path="watchlist" element={<Navigate to="/favorites" replace />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Global - also shows on the signed-out pages (sign-in etc.),
          where it's a local/per-device accent with nothing to persist
          to. AccentBar self-gates (hidden during impersonation). */}
      <AccentBar />

      {/* Visual-feedback toolbar - localhost + production-developer only, lazy. */}
      <DevAnnotation />
    </BrowserRouter>
  );
}
